import {
  createContext,
  FC,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useRef } from 'react'
import { DefaultValues, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import i18n from 'i18next'
import cloneDeep from 'lodash/cloneDeep'
import merge from 'lodash/merge'
import { Box, Collapse, Grid, Step, Stepper } from '@mui/material'

import {
  getActionsFormatted,
  getActionsForReset,
} from '@pages/GanttChart/helpers/getActionsFormatted'

import { AppLoader } from '@components/AppLoader'
import { EditConfigMenu } from '@components/ConfigEditMenu'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'
import { GeneralButtons } from '@components/GeneralButtons'

import {
  resetLeftEvents,
  resetTitle,
  setLeftEvents,
  setTitle,
} from '@redux/reducers/appFooter.reducer'

import { EditConfigProvider } from '@providers/EditConfigProvider'

import {
  useAppDispatch,
  useAuth,
  usePrompt,
  useSubscribeEvent,
  useYupValidationResolver,
} from '@hooks'
import { useDebounce } from '@hooks'
import { showSnackbarNotification } from '@helpers'
import { ENTITY_CONFIG_TYPE, USER_ROLES } from '@constants'
import { EntityCommandDTO, ObjectShortDTO } from '@types'

import {
  ConfigField,
  GanttFormValues,
  GanttFormValuesBack,
  PageContext,
  TimelineItem,
} from '@gantt/components/GanttCreateOrEdit'
import GantRowPreview from '@gantt/components/GanttCreateOrEdit/components/GanttRowPreview/GantRowPreview'
import { FORM_STEP_FIELDS, ganttConfigDefaultValue } from '@gantt/constants'
import {
  enhanceWithCommandVariables,
  getCommandsFormattedRequest,
  getCommandsFormattedResponse,
  prepareConfigData,
} from '@gantt/helpers'
import { getColumnsFormatted } from '@gantt/helpers/getColumnsFormatted'
import { useResolveFieldPath } from '@gantt/hooks'

import { ganttConfigurationSchema } from '@validations'

import { EVENT_NAME } from '../../../../../../events'

import { GanttConfigFormsRender } from './components/GanttConfigFormsRender'
import { getIsStepFailed, getStepErrorFields } from './components/StepWrap/utils'
import { StepLabelWrap } from './components'
import { parseRsqlFields } from './helpers'
import { useGanttConfiguration } from './hooks'

export const GanttSyncLoadingContext = createContext<Set<string>>(new Set())
export const FILTER_DEBOUNCE_MS = 1500

const steps = [
  i18n.t('ganttCreate.generalForm.step'),
  i18n.t('ganttCreate.resourceForm.step'),
  i18n.t('ganttCreate.timelineForm.step'),
  i18n.t('ganttCreate.mainObjectForm.step'),
  i18n.t('ganttCreate.parametersForm.step'),
]

export const GanttConfiguration: FC = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { code } = useParams()
  const navigate = useNavigate()
  const { hasRole } = useAuth()

  const isEdit = !!code
  const isAdmin = hasRole([USER_ROLES.ADMIN])
  const cameFromGantt = searchParams.get('cameFrom')

  const { handleEdit, handleHide } = useContext(PageContext)

  const [currentTabResource, setCurrentTabResource] = useState(0)
  const [lastSavedConfig, setLastSavedConfig] = useState<GanttFormValues | null>(null)
  const [lastEditedConfig, setLastEditedConfig] = useState<GanttFormValues | null>(null)
  const [isConfigValid, setIsConfigValid] = useState<boolean | null>(null)
  const [isPreviewVisible, setIsPreviewVisible] = useState<boolean>(false)

  const [activeStep, setActiveStep] = useState(0)

  const { handleSetLoading, loading } = useGanttConfiguration()
  const resolveFieldPath = useResolveFieldPath()

  const resolver = useYupValidationResolver(ganttConfigurationSchema)

  const methods = useForm<GanttFormValues>({
    defaultValues: ganttConfigDefaultValue as DefaultValues<GanttFormValues>,
    resolver,
    mode: 'all',
  })

  const {
    watch,
    handleSubmit,
    setValue,
    reset,
    formState: { isDirty, errors },
    trigger,
  } = methods

  const watchedObjectResource = watch('resource.data')
  const watchedColumns = watch('resource.columns')
  const watchedActionsResource = watch('resource.actions')

  const handleTabChangeResource = (event: SyntheticEvent, newValue: number) => {
    setCurrentTabResource(newValue)
  }

  useEffect(() => {
    onSetWatchedObjectResource(watchedObjectResource)
  }, [watchedObjectResource])

  const {
    showDialog,
    objects,
    currentColumn,
    columnToDelete,
    currentConfiguration,
    currentActionResource,
    currentActionTimeline,
    actionResourceToDelete,
    actionTimelineToDelete,
    currentSegment,
    segmentToDelete,
    currentBarIndex,
    isSuccessCreated,
    isGanttLoading,
    addGantt,
    handleOpenDialog,
    handleCloseModal,
    onSetObjectCodeResource,
    onSetWatchedObjectResource,
    onDeleteColumn,
    onSetActionToDeleteResource,
    onSetCurrentColumnsList,
    onSetCurrentSectionsList,
    onSetCurrentActionResourceList,
    onSetCurrentActionTimelineList,
  } = useContext(PageContext)

  const isLeaveEdit = isDirty && !loading && !!currentConfiguration && isEdit
  const isLeaveCreate = isDirty && !isEdit && !isSuccessCreated

  usePrompt({ when: isLeaveEdit || isLeaveCreate })

  const modelType = isEdit ? 'editModel' : 'newModel'
  const watchSegments = watch(`timeline.${currentBarIndex}.sections`)
  const watchedActionsTimeline = watch(`timeline.${currentBarIndex}.actions`)
  const watchBars = watch(`timeline`)
  const watchResourceObject = watch(`resource.data`)
  const watchCode = watch('code')
  const watchTitle = watch('title')

  const objectsClone: ObjectShortDTO[] | undefined = structuredClone(objects)
  const usedObjectsList: string[] = [
    ...(watchBars.map(item => item.data?.id).filter(Boolean) as string[]),
    watchResourceObject?.id,
  ].filter(Boolean) as string[]
  const filteredObjects = objectsClone?.filter(item => !usedObjectsList.includes(item?.code))

  function returnToGantt() {
    navigate(`/${cameFromGantt}`)
  }

  useSubscribeEvent(EVENT_NAME.RETURN_TO_GANTT, returnToGantt, !cameFromGantt)

  useEffect(() => {
    const ganttConfigurationFormSubscription = watch(watchedConfig => {
      const mergedConfig: GanttFormValues = merge({}, ganttConfigDefaultValue, watchedConfig)

      setLastEditedConfig(mergedConfig)
    })

    return () => ganttConfigurationFormSubscription.unsubscribe()
  }, [watch])

  useEffect(() => {
    if (!isEdit) {
      dispatch(setTitle(t('ganttCreate.footerConfigNew')))
    } else {
      dispatch(setTitle(t('ganttCreate.footerConfigInfo', { code: watchCode, title: watchTitle })))
    }

    return () => {
      dispatch(resetTitle())
    }
  }, [isEdit, watchCode, watchTitle])

  useEffect(() => {
    if (!cameFromGantt) {
      return
    }

    isAdmin &&
      dispatch(
        setLeftEvents([
          {
            label: t('ganttCreate.returnToGanttBtn'),
            icon: 'format_list_bulleted',
            type: EVENT_NAME.RETURN_TO_GANTT,
          },
        ])
      )

    return () => {
      dispatch(resetLeftEvents())
    }
  }, [cameFromGantt])

  useEffect(() => {
    if (watchedObjectResource) {
      onSetObjectCodeResource(watchedObjectResource.id)
    }
  }, [watchedObjectResource])

  useEffect(() => {
    onSetCurrentColumnsList(watchedColumns)
  }, [watchedColumns])

  useEffect(() => {
    onSetCurrentSectionsList(watchSegments)
  }, [watchSegments])

  useEffect(() => {
    onSetCurrentActionResourceList(watchedActionsResource)
  }, [watchedActionsResource])

  useEffect(() => {
    onSetCurrentActionTimelineList(watchedActionsTimeline)
  }, [watchedActionsTimeline])

  useEffect(() => {
    if (currentColumn) {
      const newColumns = cloneDeep(watchedColumns)
      const columnToEditIndex = newColumns.findIndex(item => item.id === currentColumn.id)

      if (columnToEditIndex !== -1) {
        newColumns.splice(columnToEditIndex, 1, currentColumn)
      } else {
        newColumns.push(currentColumn)
      }

      setValue('resource.columns', newColumns, { shouldValidate: true, shouldDirty: true })
      handleCloseModal()
    }
  }, [currentColumn])

  useEffect(() => {
    if (currentActionResource) {
      const newActionsResource = cloneDeep(watchedActionsResource)
      const actionToEditIndex = newActionsResource.findIndex(
        item => item.id === currentActionResource.id
      )

      if (actionToEditIndex !== -1) {
        newActionsResource.splice(actionToEditIndex, 1, currentActionResource)
      } else {
        newActionsResource.push(currentActionResource)
      }

      setValue('resource.actions', newActionsResource, { shouldDirty: true })
      handleCloseModal()
    }
  }, [currentActionResource])

  useEffect(() => {
    if (currentActionTimeline) {
      const newActionsTimeline = cloneDeep(watchedActionsTimeline)

      const actionToEditIndex = newActionsTimeline.findIndex(
        item => item.id === currentActionTimeline.id
      )

      if (actionToEditIndex !== -1) {
        newActionsTimeline.splice(actionToEditIndex, 1, currentActionTimeline)
      } else {
        newActionsTimeline.push(currentActionTimeline)
      }

      setValue(`timeline.${currentBarIndex}.actions`, newActionsTimeline, { shouldDirty: true })

      handleCloseModal()
    }
  }, [currentActionTimeline])

  useEffect(() => {
    if (currentSegment) {
      const newSegments = cloneDeep(watchSegments)
      const segmentToEditIndex = newSegments.findIndex(
        item => item.link.systemName === currentSegment.link.systemName
      )

      if (segmentToEditIndex !== -1) {
        newSegments.splice(segmentToEditIndex, 1, currentSegment)
      } else {
        newSegments.push(currentSegment)
      }

      setValue(`timeline.${currentBarIndex}.sections`, newSegments, {
        shouldValidate: true,
        shouldDirty: true,
      })

      handleCloseModal()
    }
  }, [currentSegment])

  useEffect(() => {
    if (columnToDelete) {
      const newColumns = cloneDeep(watchedColumns)
      const columnToDeleteIndex = newColumns.findIndex(item => item.id === columnToDelete)

      if (columnToDeleteIndex !== -1) {
        newColumns.splice(columnToDeleteIndex, 1)
        setValue('resource.columns', newColumns, { shouldValidate: true, shouldDirty: true })
      }
    }
  }, [columnToDelete])

  useEffect(() => {
    if (actionResourceToDelete) {
      const newActionsResource = cloneDeep(watchedActionsResource)
      const actionResourceToDeleteIndex = newActionsResource.findIndex(
        item => item.id === actionResourceToDelete
      )

      if (actionResourceToDeleteIndex !== -1) {
        newActionsResource.splice(actionResourceToDeleteIndex, 1)
        setValue('resource.actions', newActionsResource, { shouldDirty: true })
      }
    }
  }, [actionResourceToDelete])

  useEffect(() => {
    if (actionTimelineToDelete) {
      const newActionsTimeline = cloneDeep(watchedActionsTimeline)
      const actionTimelineToDeleteIndex = newActionsTimeline.findIndex(
        item => item.id === actionTimelineToDelete
      )

      if (actionTimelineToDeleteIndex !== -1) {
        newActionsTimeline.splice(actionTimelineToDeleteIndex, 1)
        setValue(`timeline.${currentBarIndex}.actions`, newActionsTimeline, { shouldDirty: true })
      }
    }
  }, [actionTimelineToDelete])

  useEffect(() => {
    if (segmentToDelete) {
      const newSegments = cloneDeep(watchSegments)
      const segmentToDeleteIndex = newSegments.findIndex(
        item => item.link.systemName === segmentToDelete
      )

      if (segmentToDeleteIndex !== -1) {
        newSegments.splice(segmentToDeleteIndex, 1)
        setValue(`timeline.${currentBarIndex}.sections`, newSegments, {
          shouldValidate: true,
          shouldDirty: true,
        })
      }
    }
  }, [segmentToDelete])

  const watchBarsFilters = watch('timeline')
  const watchResourceFilter = watch('resource.filter.static')
  const watchResourceTimeEdgesFilter = watch('resource.timeEdgesFilter.static')

  const FILTER_DEBOUNCE_MS = 1500

  const debouncedResourceFilter = useDebounce(watchResourceFilter, FILTER_DEBOUNCE_MS)
  const debouncedResourceTimeEdgesFilter = useDebounce(
    watchResourceTimeEdgesFilter,
    FILTER_DEBOUNCE_MS
  )
  const debouncedBarsFiltersKey = useDebounce(
    JSON.stringify(
      watchBarsFilters?.map(tl => ({ f: tl.filter?.static, te: tl.timeEdgesFilter?.static }))
    ),
    FILTER_DEBOUNCE_MS
  )

  // Счётчик поколений на каждый formPath — защита от race condition
  const syncGenRef = useRef<Record<string, number>>({})

  const [loadingFormPaths, setLoadingFormPaths] = useState<Set<string>>(new Set())

  const syncSystemFieldsForPath = async (
    rsqlSources: Array<{ rsql: string; comment: string }>,
    objectCode: string,
    formPath: string
  ) => {
    const gen = (syncGenRef.current[formPath] ?? 0) + 1
    syncGenRef.current[formPath] = gen

    const parsedFields = rsqlSources.flatMap(({ rsql, comment }) =>
      parseRsqlFields(rsql).map(f => ({ ...f, comment }))
    )

    if (parsedFields.length > 0) {
      setLoadingFormPaths(prev => new Set(prev).add(formPath))
    }

    const resolvedFields = await Promise.all(
      parsedFields.map(async f => {
        const pathArray = objectCode ? await resolveFieldPath(f.pathStr, objectCode) : []

        return { ...f, isSystem: true, pathResolved: pathArray.length > 0, pathArray }
      })
    )

    // Если за время async-операций появился более новый вызов — отбрасываем результат
    if (syncGenRef.current[formPath] !== gen) return

    setLoadingFormPaths(prev => {
      const next = new Set(prev)
      next.delete(formPath)

      return next
    })

    const current: ConfigField[] = watch(formPath) ?? []
    const userFields = current.filter((f: ConfigField) => !f.isSystem)
    const userPathStrs = new Set(userFields.map((f: ConfigField) => f.pathStr))
    const newSystemFields = resolvedFields.filter(
      f => f.pathResolved && !userPathStrs.has(f.pathStr)
    )

    setValue(formPath, [...userFields, ...newSystemFields])
  }

  useEffect(() => {
    syncSystemFieldsForPath(
      [
        {
          rsql: typeof debouncedResourceFilter === 'string' ? debouncedResourceFilter : '',
          comment: t('ganttCreate.common.systemVariableCommentFilter', { axis: 'Y' }),
        },
        {
          rsql:
            typeof debouncedResourceTimeEdgesFilter === 'string'
              ? debouncedResourceTimeEdgesFilter
              : '',
          comment: t('ganttCreate.common.systemVariableCommentTimeEdges', { axis: 'Y' }),
        },
      ],
      watch('resource.data')?.id ?? '',
      'resource.additionalFields'
    )
  }, [debouncedResourceFilter, debouncedResourceTimeEdgesFilter])

  useEffect(() => {
    watchBarsFilters?.forEach((tl: any, barIndex: number) => {
      const filterRsql = tl.filter?.static
      const timeEdgesRsql = tl.timeEdgesFilter?.static

      syncSystemFieldsForPath(
        [
          {
            rsql: typeof filterRsql === 'string' ? filterRsql : '',
            comment: t('ganttCreate.common.systemVariableCommentFilter', { axis: 'X' }),
          },
          {
            rsql: typeof timeEdgesRsql === 'string' ? timeEdgesRsql : '',
            comment: t('ganttCreate.common.systemVariableCommentTimeEdges', { axis: 'X' }),
          },
        ],
        tl.data?.id ?? '',
        `timeline.${barIndex}.additionalFields`
      )
    })
  }, [debouncedBarsFiltersKey])

  const getSystemFieldPathStrs = (config: GanttFormValues | null): Set<string> => {
    if (!config) return new Set()

    return new Set([
      ...(config.resource?.additionalFields
        ?.filter((f: ConfigField) => f.isSystem)
        .map((f: ConfigField) => f.pathStr) ?? []),
      ...(config.timeline?.flatMap(
        tl =>
          tl.additionalFields
            ?.filter((f: ConfigField) => f.isSystem)
            .map((f: ConfigField) => f.pathStr) ?? []
      ) ?? []),
    ])
  }

  const onSubmit = (data: GanttFormValues) => {
    const currentSystemPaths = getSystemFieldPathStrs(data)
    const savedSystemPaths = getSystemFieldPathStrs(lastSavedConfig)
    const hasNewSystemFields = [...currentSystemPaths].some(p => !savedSystemPaths.has(p))

    if (hasNewSystemFields) {
      showSnackbarNotification({
        text: t('ganttCreate.filterVariablesInfo'),
        options: { variant: 'info', autoHideDuration: 7000 },
      })
    }

    const preparedResource: GanttFormValuesBack['resource'] = {
      ...data.resource,
      columns: getColumnsFormatted(data.resource.columns),
      commands: getCommandsFormattedRequest(data.resource.commands),
    }

    const preparedTimeline: GanttFormValuesBack['timeline'] = data.timeline.map(timeline => ({
      ...timeline,
      actions: getActionsFormatted(timeline.actions),
      commands: getCommandsFormattedRequest(timeline.commands),
    }))

    const newData: GanttFormValuesBack = {
      code: data.code,
      title: data.title,
      canvas: data.canvas,
      resource: preparedResource, // ЛЕВАЯ ЧАСТЬ - Ресурсы
      timeline: preparedTimeline, // ПРАВАЯ ЧАСТЬ - Бары
      sort: data.sort,
    }

    if (isEdit) {
      newData.id = code
    }

    const preparedConfigData: GanttFormValues = prepareConfigData(newData)
    const enhancedWithCommandVariables: GanttFormValues =
      enhanceWithCommandVariables(preparedConfigData)

    addGantt(enhancedWithCommandVariables)
  }

  useEffect(() => {
    if (currentConfiguration && objects) {
      const responseConfig = currentConfiguration

      const preparedResource = {
        ...responseConfig.resource,
        commands: getCommandsFormattedResponse(responseConfig.resource?.commands),
      }

      const preparedTimeline: TimelineItem[] = (
        responseConfig.timeline as (TimelineItem & { commands: EntityCommandDTO[] })[]
      ).map(timeline => ({
        ...timeline,
        isMergeSegments: timeline?.isMergeSegments ?? false,
        commands: getCommandsFormattedResponse(timeline?.commands),
        actions: getActionsForReset(timeline?.actions),
      }))

      const mergedConfig = merge({}, ganttConfigDefaultValue, currentConfiguration)
      const lastSavedConfig: GanttFormValues = {
        ...mergedConfig,
        timeline: preparedTimeline,
        resource: preparedResource,
      }

      reset(lastSavedConfig)
      setLastSavedConfig(lastSavedConfig)
      handleSetLoading(false)

      return
    }
  }, [currentConfiguration, objects])

  useEffect(() => {
    trigger().then(isValid => {
      setIsConfigValid(isValid)
    })
  }, [lastEditedConfig])

  const handleCancel = useCallback(
    () => lastSavedConfig && reset(lastSavedConfig),
    [reset, lastSavedConfig]
  )

  const handleClickStep = (index: number) => {
    for (let i = 0; i < index; i++) {
      FORM_STEP_FIELDS[i].map(item => trigger(item))
    }

    setActiveStep(index)
  }

  const handleTogglePreviewVisibility = () => {
    trigger().then(isValid => {
      setIsConfigValid(isValid)
    })

    setIsPreviewVisible(prev => !prev)
  }

  return (
    <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.GANTT_CHARTS}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: 1,
            p: 3,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            pb: '150px',
          }}
        >
          {loading ? (
            <AppLoader />
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <GanttSyncLoadingContext.Provider value={loadingFormPaths}>
                <FormProvider {...methods}>
                  <EditConfigScriptDialog />
                  <Grid container alignItems={'center'} justifyContent={'flex-end'}>
                    {code && <EditConfigMenu configId={code} />}
                  </Grid>
                  <Stepper alternativeLabel activeStep={activeStep}>
                    {steps.map((label, index) => (
                      <Step key={label} disabled={false}>
                        <StepLabelWrap
                          key={label}
                          errors={errors}
                          getIsStepFailed={getIsStepFailed}
                          getStepErrorFields={getStepErrorFields}
                          label={label}
                          onClick={() => handleClickStep(index)}
                        />
                      </Step>
                    ))}
                  </Stepper>

                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <GanttConfigFormsRender
                      activeStep={activeStep}
                      currentTabResource={currentTabResource}
                      filteredObjects={filteredObjects}
                      isDisabledResource={!watch('resource.data')}
                      isLoading={loading}
                      isShowDialog={showDialog}
                      modelType={modelType}
                      objects={objects}
                      rowsActionTableResource={watchedActionsResource}
                      rowsColumnTable={watchedColumns}
                      usedObjectsList={usedObjectsList || []}
                      onDeleteActionResource={onSetActionToDeleteResource}
                      onDeleteColumn={onDeleteColumn}
                      onEdit={handleEdit}
                      onHide={handleHide}
                      onOpenDialog={handleOpenDialog}
                      onTabChangeResource={handleTabChangeResource}
                    />
                  </Box>
                </FormProvider>
              </GanttSyncLoadingContext.Provider>
            </div>
          )}
        </Box>

        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            width: '100%',
            flexShrink: 0,
          }}
        >
          <Grid
            container
            direction='column'
            sx={{
              background: 'white',
              borderTop: '1px solid',
              borderColor: 'grey.300',
              borderRadius: '4px 4px 0 0',
              boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
            }}
          >
            <Grid item sx={{ width: '100%' }}>
              <Collapse
                in={isPreviewVisible}
                timeout={300}
                easing={{
                  enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
                  exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                sx={{
                  transformOrigin: 'top',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: 'fit-content',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <GantRowPreview config={lastEditedConfig} isConfigValid={isConfigValid} />
                </Box>
              </Collapse>
            </Grid>

            <Grid item>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  p: '5px',
                  borderTop: '1px solid',
                  borderColor: 'grey.300',
                }}
              >
                <GeneralButtons
                  cancelDisabled={!lastSavedConfig}
                  cancelText={t('viewOrFormCreate.cancelBtnText')}
                  isPreviewVisible={isPreviewVisible}
                  saveDisabled={isGanttLoading}
                  saveText={t('viewOrFormCreate.saveBtnText')}
                  onCancel={handleCancel}
                  onSave={handleSubmit(onSubmit)}
                  onTogglePreviewVisibility={handleTogglePreviewVisibility}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </EditConfigProvider>
  )
}
